import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { GitBranch, FileText, RefreshCw, ExternalLink } from "lucide-react";

const REL_STYLES = {
  amendment: { icon: FileText,  color: "bg-amber-50 text-amber-800 border-amber-200",     label: "Amendment" },
  addendum:  { icon: FileText,  color: "bg-blue-50 text-blue-800 border-blue-200",         label: "Addendum" },
  renewal:   { icon: RefreshCw, color: "bg-emerald-50 text-emerald-800 border-emerald-200",label: "Renewal" },
  parent:    { icon: GitBranch, color: "bg-slate-100 text-slate-700 border-slate-200",     label: "Parent Contract" },
  linked:    { icon: GitBranch, color: "bg-slate-100 text-slate-700 border-slate-200",     label: "Linked" },
};

/** Card showing every contract linked to this one — parent + children.
 *  Renders below the header on ContractDetail so the linkage is unmissable. */
export default function LinkedContractsPanel({ contractId, onLinkParent, onLinkChildren, canLink }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!contractId) return;
    api.get(`/contracts/${contractId}/linked`).then(r => setData(r.data)).catch(() => setData(null));
  }, [contractId]);

  const hasAny = data && (!!data.parent || (data.children || []).length > 0);

  if (!data) return null;
  // If nothing to show AND user can't link, hide entirely
  if (!hasAny && !canLink) return null;

  const groups = data ? [
    ...(data.parent ? [{ ...data.parent, relation: "parent" }] : []),
    ...(data.children || []),
  ] : [];

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-md p-4" data-testid="linked-panel">
      <div className="flex items-center gap-2 mb-3">
        <GitBranch className="w-4 h-4 text-[#005289]" />
        <div className="text-sm font-bold text-slate-900">Linked Contracts</div>
        <div className="text-[10px] text-slate-500">{groups.length} related</div>
        {canLink && onLinkParent && (
          <button onClick={onLinkParent} data-testid="linked-add-parent-btn"
                  className="ml-auto h-7 px-2.5 text-[11px] font-semibold border border-[#005289]/40 text-[#005289] rounded hover:bg-[#005289]/5 flex items-center gap-1">
            <GitBranch className="w-3 h-3" /> Link to parent
          </button>
        )}
        {canLink && onLinkChildren && (
          <button onClick={onLinkChildren} data-testid="linked-add-children-btn"
                  className={`h-7 px-2.5 text-[11px] font-semibold border border-[#005289]/40 text-[#005289] rounded hover:bg-[#005289]/5 flex items-center gap-1 ${(canLink && onLinkParent) ? "" : "ml-auto"}`}>
            <GitBranch className="w-3 h-3" /> Link children
          </button>
        )}
      </div>
      {groups.length === 0 ? (
        <div className="text-xs text-slate-400 italic text-center py-3">No parent set. Click "Link to parent" to wire this contract to its MSA.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {groups.map(g => {
            const s = REL_STYLES[g.relation] || REL_STYLES.linked;
            const Icon = s.icon;
            return (
              <button key={g.id} onClick={() => nav(`/contract/${g.id}`)}
                      data-testid={`linked-${g.relation}-${g.contract_number}`}
                      className={`text-left border rounded p-2.5 flex items-center gap-2 hover:shadow-sm ${s.color}`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-bold uppercase tracking-wider">{s.label}</span>
                    <span className="text-xs font-mono">#{g.contract_number}</span>
                  </div>
                  <div className="text-xs font-semibold truncate">{g.vendor}</div>
                  <div className="text-[10px] opacity-70">{(g.stage || "").replace(/_/g, " ")}</div>
                </div>
                <ExternalLink className="w-3 h-3 opacity-60 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
